import { useInternalState, assignMswData, serializeQuery } from '../hooks/internalState';
import {
  PutMeGroupResult,
  PutMeGroupOptions,
  DeleteMeGroupResult,
  DeleteMeGroupOptions,
  GetMeGroupOptions,
  GetMeGroupResult,
  getMeGroup,
  PutMeGroupFields,
  DeleteMeGroupFields,
  GetMeGroupFields,
  putMeGroup,
  deleteMeGroup,
} from '../fetchers/me/group';
import { useCallback } from 'react';
import useSWR, { SWRConfiguration, useSWRConfig } from 'swr';
import { useGctlConfig } from '../hooks/GctlProvider';

/**
 * DO NOT MODIFY
 * Auto-generated by gctl-plugin-vimeo-hooks
 */

type UseGetMeGroupOptions<Fields extends GetMeGroupFields> = GetMeGroupOptions<Fields>;

/**
 * Check if a user has joined a group
 * This method determines whether the authenticated user belongs to the specified group.
 */

export function useGetMeGroup<Fields extends GetMeGroupFields>(
  getOptions: UseGetMeGroupOptions<Fields> | (() => UseGetMeGroupOptions<Fields> | null),
  config?: SWRConfiguration<GetMeGroupResult<Fields>>,
) {
  const options = typeof getOptions === 'function' ? getOptions() : getOptions;

  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  return useSWR(
    options ? `/me/groups/${options.where.groupId}${serializeQuery(options)}` : () => null,
    options
      ? () => {
          return getMeGroup({
            ...options,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
            baseUrl,
          });
        }
      : null,
    config,
  );
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetMeGroup, {
    endpoint: '/me/groups/:groupId',
    method: 'GET',
  });
}

/**
 * Check if a user has joined a group
 * This method determines whether the authenticated user belongs to the specified group.
 */

export function useGetMeGroupLazy<Fields extends GetMeGroupFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<GetMeGroupResult<Fields> | undefined>();

  let callback = useCallback(
    async (options: UseGetMeGroupOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/groups/${options.where.groupId}${serializeQuery(options)}`,
          getMeGroup({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [callback, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useGetMeGroupLazy, {
    endpoint: '/me/groups/:groupId',
    method: 'GET',
  });
}

type UsePutMeGroupOptions<Fields extends PutMeGroupFields> = PutMeGroupOptions<Fields>;

/**
 * Add the user to a group
 * This method adds the authenticated user to the specified group.
 */

export function usePutMeGroup<Fields extends PutMeGroupFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<PutMeGroupResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UsePutMeGroupOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/groups/${options.where.groupId}${serializeQuery(options)}`,
          putMeGroup({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(usePutMeGroup, {
    endpoint: '/me/groups/:groupId',
    method: 'PUT',
  });
}

type UseDeleteMeGroupOptions<Fields extends DeleteMeGroupFields> = DeleteMeGroupOptions<Fields>;

/**
 * Remove the user from a group
 * This method removes the authenticated user from the specified group. The authenticated user can't be the owner of the group; assign a new owner through a PATCH request first.
 */

export function useDeleteMeGroup<Fields extends DeleteMeGroupFields>() {
  const { mutate } = useSWRConfig();
  let { baseUrl, jwt, xVimeoPage, locale } = useGctlConfig();

  let [state, dispatch] = useInternalState<DeleteMeGroupResult<Fields> | undefined>();

  let call = useCallback(
    async (options: UseDeleteMeGroupOptions<Fields>) => {
      dispatch({ type: 'REQUEST' });

      try {
        let result = await mutate(
          `/me/groups/${options.where.groupId}${serializeQuery(options)}`,
          deleteMeGroup({
            ...options,
            baseUrl,
            headers: {
              ...options.headers,
              'Content-Type': 'application/json',
              Authorization: jwt ? `jwt ${jwt}` : '',
              'Vimeo-Page': `${xVimeoPage}`,
              'Accept-Language': locale ?? 'en',
            },
          }),
          false,
        );

        dispatch({ type: 'SUCCESS', payload: result });
      } catch (err) {
        dispatch({ type: 'FAILURE', payload: err });
      }
    },
    [baseUrl, xVimeoPage, jwt, locale, dispatch],
  );

  return [call, state] as const;
}

if (process.env.NODE_ENV !== 'production' || process.env.STORYBOOK === 'true') {
  assignMswData(useDeleteMeGroup, {
    endpoint: '/me/groups/:groupId',
    method: 'DELETE',
  });
}
